// @ts-check
/**
 * Service for detecting barcodes using the Barcode Detection API
 * https://developer.mozilla.org/en-US/docs/Web/API/Barcode_Detection_API
 * Falls back to ZXing.js when the native API is not available
 */

import ZXingBarcodeService from "./ZXingBarcodeService";
import type { CameraSlotConfig } from "./StorageService";

// Check if the browser supports the Barcode Detection API
export const isBarcodeDetectionSupported = (): boolean => {
  return "BarcodeDetector" in window;
};

// Define supported formats
export type BarcodeFormat =
  | "aztec"
  | "code_128"
  | "code_39"
  | "code_93"
  | "codabar"
  | "data_matrix"
  | "ean_13"
  | "ean_8"
  | "itf"
  | "pdf417"
  | "qr_code"
  | "upc_a"
  | "upc_e";

// Interface for detection result
export interface BarcodeDetectionResult {
  code: string;
  format: string;
  cornerPoints?: Array<{ x: number; y: number }>;
}

export default class BarcodeDetectionService {
  private detector: unknown; // BarcodeDetector
  private isSupported: boolean;
  private scanInterval: number | null = null;
  private isScanning = false;
  private fallbackService: ZXingBarcodeService | null = null;
  private streamCounter = 0; // For generating unique stream IDs
  private activeStreams: Map<number, string> = new Map(); // Maps camera index to ZXing stream ID
  private debugMode: boolean = false;

  constructor(
    formats: BarcodeFormat[] = [
      "code_128",
      "code_39",
      "ean_13",
      "ean_8",
      "upc_a",
      "upc_e",
      "qr_code",
    ],
    options?: { debug?: boolean }
  ) {
    this.isSupported = isBarcodeDetectionSupported();
    this.debugMode = options?.debug || false;

    if (this.isSupported) {
      try {
        // @ts-expect-error - TypeScript doesn't know about BarcodeDetector yet
        this.detector = new BarcodeDetector({ formats });
      } catch (error) {
        console.error("Error initializing BarcodeDetector:", error);
        this.isSupported = false;
      }
    } else {
      console.log(
        "Native Barcode Detection API not supported, using ZXing fallback"
      );
      this.fallbackService = new ZXingBarcodeService({ debug: this.debugMode });
    }
  }

  /**
   * Check if the service is supported in this browser
   */
  public isApiSupported(): boolean {
    return this.isSupported || this.fallbackService !== null;
  }

  /**
   * Check if the API is supported (static method)
   */
  public static isApiSupported(): boolean {
    return (
      isBarcodeDetectionSupported() || ZXingBarcodeService.isApiSupported()
    );
  }

  /**
   * Detect barcodes in an image or video frame
   */
  public async detectBarcodes(
    imageSource:
      | HTMLVideoElement
      | HTMLImageElement
      | HTMLCanvasElement
      | ImageBitmap
  ): Promise<BarcodeDetectionResult[]> {
    if (this.isSupported && this.detector) {
      try {
        // Use native API
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        const barcodes = await this.detector.detect(imageSource);

        return barcodes.map(
          (barcode: {
            rawValue: string;
            format: string;
            cornerPoints: never;
          }) => ({
            code: barcode.rawValue,
            format: barcode.format,
            cornerPoints: barcode.cornerPoints,
          })
        );
      } catch (error) {
        console.error("Error detecting barcodes with native API:", error);
      }
    }

    // Fall back to ZXing if native failed or not supported
    if (this.fallbackService) {
      // For one-shot detection, we would need to implement a method in ZXingBarcodeService
      // This would require substantial work as ZXing is currently set up for continuous scanning
      console.warn("One-shot barcode detection with ZXing is not implemented");
    }

    return [];
  }

  /**
   * Start continuous scanning from a video element
   */
  public startContinuousScan(
    videoElement: HTMLVideoElement,
    onDetect: (results: BarcodeDetectionResult[]) => void,
    scanFrequency = 500, // ms
    options?: {
      isExternalCamera?: boolean;
      debugElement?: HTMLElement;
      slotConfig?: CameraSlotConfig; // Per-slot enhancement configuration
    }
  ): void {
    if (this.isScanning) {
      this.stopContinuousScan();
    }

    // Detect if this might be an external camera based on label or resolution
    const isLikelyExternal =
      options?.isExternalCamera || this.detectExternalCamera(videoElement);

    if (isLikelyExternal) {
      console.log(
        "Detected likely external camera, applying optimized settings"
      );
    }

    if (this.isSupported) {
      // Using native API
      this.isScanning = true;

      // Function to perform scan
      const scan = async () => {
        if (!this.isScanning || !videoElement.readyState) return;

        try {
          const barcodes = await this.detectBarcodes(videoElement);
          if (barcodes.length > 0) {
            onDetect(barcodes);
          }
        } catch (error) {
          console.error("Error in continuous scan:", error);
        }
      };

      // Start the scanning interval
      this.scanInterval = window.setInterval(scan, scanFrequency);
    } else if (this.fallbackService) {
      // Use ZXing fallback with external camera optimizations
      console.log(
        `Using ZXing fallback for continuous scanning${
          isLikelyExternal ? " (external camera)" : ""
        }`
      );

      // Generate a unique stream ID for this video element
      const streamId = this.fallbackService.startContinuousScan(
        videoElement,
        onDetect,
        scanFrequency,
        {
          isExternalCamera: isLikelyExternal,
          debugElement: options?.debugElement,
          slotConfig: options?.slotConfig, // Pass slot config to ZXing
        }
      );

      // Store the stream ID to reference it later for stopping
      this.activeStreams.set(this.streamCounter++, streamId);
      this.isScanning = true;
    }
  }

  /**
   * Try to detect if a video element might be connected to an external camera
   * based on its properties
   */
  private detectExternalCamera(videoElement: HTMLVideoElement): boolean {
    try {
      // Check if there's a srcObject with tracks
      const stream = videoElement.srcObject as MediaStream;

      if (stream && stream.getVideoTracks().length > 0) {
        const track = stream.getVideoTracks()[0];

        // Check track label for common external camera indicators
        const label = track.label.toLowerCase();
        const externalKeywords = [
          "usb",
          "external",
          "webcam",
          "logitech",
          "microsoft",
          "cam",
        ];

        // Check settings for high resolution (likely external)
        const settings = track.getSettings();
        const isHighRes = (settings.width && settings.width > 1280) ?? false;

        // Check if any external camera keywords are in the label
        const hasExternalKeyword = externalKeywords.some((keyword) =>
          label.includes(keyword)
        );

        return hasExternalKeyword || Boolean(isHighRes);
      }
    } catch (error) {
      console.error("Error detecting external camera:", error);
    }

    return false;
  }

  /**
   * Enable or disable debug mode
   */
  public setDebugMode(enabled: boolean): void {
    this.debugMode = enabled;

    if (this.fallbackService) {
      this.fallbackService.setDebugMode(enabled);
    }

    console.log(
      `Barcode detection debug mode ${enabled ? "enabled" : "disabled"}`
    );
  }

  /**
   * Stop continuous scanning
   */
  public stopContinuousScan(cameraIndex?: number): void {
    if (this.isSupported) {
      // Using native API
      if (this.scanInterval) {
        clearInterval(this.scanInterval);
        this.scanInterval = null;
      }
    } else if (this.fallbackService) {
      // Using ZXing fallback
      if (cameraIndex !== undefined && this.activeStreams.has(cameraIndex)) {
        // Stop a specific stream
        const streamId = this.activeStreams.get(cameraIndex);
        if (streamId) {
          this.fallbackService.stopContinuousScanById(streamId);
          this.activeStreams.delete(cameraIndex);
        }
      } else {
        // Stop all streams
        this.fallbackService.stopContinuousScan();
        this.activeStreams.clear();
      }
    }

    if (cameraIndex === undefined || !this.activeStreams.size) {
      this.isScanning = false;
    }
  }

  /**
   * Get supported formats (if available)
   */
  public async getSupportedFormats(): Promise<string[]> {
    if (this.isSupported) {
      try {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        return await BarcodeDetector.getSupportedFormats();
      } catch (error) {
        console.error("Error getting supported formats:", error);
      }
    }

    // Fallback to ZXing supported formats
    if (this.fallbackService) {
      return this.fallbackService.getSupportedFormats();
    }

    return [];
  }

  /**
   * Get supported formats (static method)
   */
  public static async getSupportedFormats(): Promise<string[]> {
    if (isBarcodeDetectionSupported()) {
      try {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        return await BarcodeDetector.getSupportedFormats();
      } catch (error) {
        console.error("Error getting supported formats:", error);
      }
    }

    // Return ZXing supported formats when native API is not available
    return [
      "qr_code",
      "code_128",
      "code_39",
      "ean_13",
      "ean_8",
      "upc_a",
      "upc_e",
      "data_matrix",
    ];
  }
}
